import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../api/auth';
import FaceLogin from '../components/auth/FaceLogin';

// ─── Login Helios — Face ID + Email/Password ────────────────────────────────

type LoginMode = 'face' | 'password';

/**
 * CE QUI A ÉCHOUÉ — ET SURTOUT, DE QUEL CÔTÉ.
 *
 * ⚠️⚠️ MESURÉ LE 14/09/2026. Cet écran affichait « Identifiants incorrects. » à
 * quelqu'un dont la requête n'était JAMAIS PARTIE : le bundle appelait
 * `helios.mowibo.com` depuis `app.xawio.fr`, et le préflight CORS la bloquait.
 * Aucun POST n'atteignait le serveur, et la personne réessayait son mot de passe
 * juste, indéfiniment, en croyant s'être trompée.
 *
 * ⚠️ LA CAUSE EST DANS LE REPLI, PAS DANS LE MESSAGE. L'ancien code lisait
 * `error?.response?.data?.detail` et retombait sur l'accusation quand il ne
 * trouvait rien. Or une erreur RÉSEAU n'a pas de `response` du tout : le cas « je
 * n'ai pas pu demander » tombait donc dans la branche « on t'a répondu non ».
 * Les deux se ressemblent à l'écran ; ils appellent des gestes opposés —
 * retaper son mot de passe, ou prévenir l'exploitant.
 */
function messageDErreur(erreur: unknown): string {
  const e = erreur as { response?: { status?: number; data?: { detail?: string } }; message?: string } | null;

  // Pas de réponse du tout : réseau, CORS, service à l'arrêt. Ce n'est pas la
  // personne qui est en cause, et le lui laisser croire est le défaut corrigé ici.
  if (!e?.response) {
    return "Le service n'a pas répondu. Vos identifiants ne sont pas en cause — "
      + "réessayez dans un moment, et signalez-le si cela persiste.";
  }
  if (e.response.status === 401) return 'Identifiants incorrects.';
  if (e.response.status === 429) return 'Trop de tentatives. Patientez avant de réessayer.';

  // Le serveur a répondu autre chose : on RAPPORTE ce qu'il dit plutôt que de le
  // traduire en refus d'identifiants.
  return e.response.data?.detail
    ?? `Le service a signalé une erreur (${e.response.status}).`;
}

export default function LoginPage() {
  // Defaut 'password' : Face ID requiert un visage enrole en DB Helios,
  // sinon affiche "Aucun visage detecte" qui peut etre confondu avec "Not Found".
  // L utilisateur peut basculer via le bouton "Face ID".
  const [mode, setMode] = useState<LoginMode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const loginMutation = useLogin();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    loginMutation.mutate(
      { email, password },
      { onSuccess: () => navigate('/dashboard', { replace: true }) }
    );
  };

  return (
    <div className="login-bg min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-gray-900/80 backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 mb-3 shadow-lg shadow-amber-500/20">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">Xawio</h1>
            <p className="text-gray-500 text-xs mt-0.5">
              {mode === 'face' ? 'Authentification biometrique' : 'Connexion securisee'}
            </p>
          </div>

          {/* ═══ MODE FACE ID ═══ */}
          {mode === 'face' && (
            <FaceLogin
              onSuccess={() => navigate('/dashboard', { replace: true })}
              onFallback={() => setMode('password')}
            />
          )}

          {/* ═══ MODE PASSWORD ═══ */}
          {mode === 'password' && (
            <>
              {/* Toggle Face ID */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setMode('face')}
                  aria-label="Basculer vers authentification Face ID"
                  className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 bg-amber-500/10 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" />
                  </svg>
                  Face ID
                </button>
              </div>

              {/* Error */}
              {loginMutation.isError && (
                <div role="alert" aria-live="assertive" className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm text-center">
                  {messageDErreur(loginMutation.error)}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Adresse email
                  </label>
                  <input
                    id="email" type="email" required autoComplete="email" autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-800/60 border border-gray-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Mot de passe
                  </label>
                  <input
                    id="password" type="password" required autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-2.5 rounded-lg bg-gray-800/60 border border-gray-600/50 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loginMutation.isPending}
                  className="w-full py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold hover:from-amber-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {loginMutation.isPending ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Connexion...
                    </>
                  ) : 'Se connecter'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          Xawio &copy; {new Date().getFullYear()} Mowibo — Biometric Auth
        </p>
      </div>

      <style>{`
        .login-bg {
          background: #0a0a0f;
          position: relative;
          overflow: hidden;
        }
        .login-bg::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(ellipse at 20% 50%, rgba(245, 158, 11, 0.08) 0%, transparent 50%),
                      radial-gradient(ellipse at 80% 20%, rgba(234, 88, 12, 0.06) 0%, transparent 50%),
                      radial-gradient(ellipse at 50% 80%, rgba(168, 85, 247, 0.04) 0%, transparent 50%);
          animation: loginGradient 15s ease-in-out infinite alternate;
          pointer-events: none;
        }
        @keyframes loginGradient {
          0% { transform: translate(0%, 0%) rotate(0deg); }
          33% { transform: translate(2%, -1%) rotate(1deg); }
          66% { transform: translate(-1%, 2%) rotate(-1deg); }
          100% { transform: translate(1%, -2%) rotate(0.5deg); }
        }
      `}</style>
    </div>
  );
}
